import { useState } from 'react';
import { Alert, Button, Tooltip } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { QueryKey, useQueryClient } from '@tanstack/react-query';

import { useLocalization } from 'localization';
import { colors } from 'theme/mui/colors';
import { isEmpty } from 'lodash';

type Props = {
  onRetry?: () => void;
  errorMessage?: string;
  isLoading?: boolean;
  queryKey?: readonly QueryKey[];
};

const FetchingErrorAlert = (props: Props) => {
  const { t } = useLocalization();
  const { onRetry, errorMessage = t('common.messages.fetchingError'), isLoading, queryKey } = props;
  const [isRetry, setIsRetry] = useState(false);

  const queryClient = useQueryClient();

  const handleRetry = async () => {
    setIsRetry(true);
    try {
      if (queryKey?.length) {
        await Promise.all(queryKey.map(key => queryClient.invalidateQueries({ queryKey: key })));
      }
      onRetry?.();
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRetry(false);
    }
  };

  return (
    <Alert
      severity="error"
      sx={{
        width: 'fit-content',
        alignItems: 'center',
        '& .MuiAlert-action': {
          py: 0,
        },
      }}
      action={
        (onRetry || !isEmpty(queryKey)) && (
          <Tooltip title={t('common.actions.retry')}>
            <Button
              onClick={handleRetry}
              loading={isLoading || isRetry}
              color="inherit"
              size="small"
              sx={{
                fontSize: '0.875rem',
                '& .MuiButton-loadingIndicator': {
                  color: colors.primary.main,
                },
              }}
            >
              <Refresh fontSize="small" />
            </Button>
          </Tooltip>
        )
      }
    >
      {errorMessage}
    </Alert>
  );
};

export default FetchingErrorAlert;
